import { useState } from "react";
import { toast } from "react-toastify";
import { API_URL } from "../../creds";

const ImageUploadModal = ({ isOpen, onClose, onTextExtracted }) => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedText, setExtractedText] = useState("");
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [zoom, setZoom] = useState(1); // 1 = 100%

  if (!isOpen) return null;

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setExtractedText("");
    setAnnotatedImage(null);
    setZoom(1);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setExtractedText("");
      setAnnotatedImage(null);
      setZoom(1);
    }
  };

  const handleSubmit = async () => {
    if (!image) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("image", image);

    try {
      const response = await fetch(`${API_URL}/vision_ocr`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "OCR failed");

      const text = result.extracted_text || "";
      const annotatedUrl = result.annotated_image_url
        ? `${API_URL}${result.annotated_image_url}`
        : null;

      setExtractedText(text || "No text detected");
      setAnnotatedImage(annotatedUrl);
      setZoom(1);
      toast.success("Text extracted successfully!");
    } catch (err) {
      toast.error(err.message || "Failed to extract text");
      setExtractedText("Error: Could not read text");
    } finally {
      setLoading(false);
    }
  };

  const handleUseText = () => {
    const finalText = extractedText.trim();
    if (finalText && finalText !== "No text detected" && !finalText.includes("Error")) {
      onTextExtracted(finalText);
      toast.success("Text loaded for translation!");
      onClose();
    } else {
      toast.error("No valid text to use");
    }
  };

  const resetAndClose = () => {
    setImage(null);
    setPreview(null);
    setExtractedText("");
    setAnnotatedImage(null);
    setZoom(1);
    onClose();
  };

  const zoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setZoom(1);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-3xl max-w-5xl w-full max-h-screen overflow-y-auto">
        <div className="px-4 py-2">

          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold text-gray-800">Image to Text (OCR)</h2>
            <button onClick={resetAndClose} className="text-gray-500 hover:text-red-600 text-3xl">
              X
            </button>
          </div>

          {/* Upload Area - Only before extraction */}
          {!extractedText && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-4 border-dashed border-blue-400 rounded-2xl p-12 mx-20 my-10 text-center cursor-pointer hover:border-blue-600 transition bg-blue-50"
              onClick={() => document.getElementById("imageInput").click()}
            >
              <input id="imageInput" type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              {!preview ? (
                <>
                  <div className="text-4xl mb-4 text-blue-600">Upload Image</div>
                  <p className="text-xl text-gray-900">Click or drag & drop your image</p>
                  <p className="text-sm text-gray-900 mt-2">JPG, PNG, WebP supported</p>
                </>
              ) : (
                <>
                  <>
                    <img src={preview} alt="Preview" className="max-w-full max-h-96 mx-auto rounded-xl shadow-2xl" />
                    <p className="mt-6 text-lg font-semibold text-gray-900">{image.name}</p>
                  </>
                </>
              )}
            </div>
          )}

          {/* Result View - After extraction */}
          {extractedText && (
            <div className="grid lg:grid-cols-2 gap-2 mt-2">

              {/* Annotated Image with Zoom */}
              {annotatedImage && (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 py-1 px-2 rounded-2xl border-2 border-emerald-200">
                  <div className="flex justify-between items-center mb-1">
                    <h3 className="text-xl font-bold text-emerald-800">Detected Text</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={zoomOut} className="p-2 bg-white rounded-lg shadow hover:bg-gray-100">−</button>
                      <span className="font-bold">{Math.round(zoom * 100)}%</span>
                      <button onClick={zoomIn} className="p-2 bg-white rounded-lg shadow hover:bg-gray-100">+</button>
                      <button onClick={resetZoom} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg">
                        Reset
                      </button>
                    </div>
                  </div>
                  <div className="overflow-auto max-h-96 rounded-xl border-4 border-emerald-300 bg-white p-4">
                    <img
                      src={annotatedImage}
                      alt="With boxes"
                      style={{ transform: `scale(${zoom})`, transition: "transform 0.3s ease" }}
                      className="origin-top-left"
                    />
                  </div>
                </div>
              )}

              {/* Editable Text */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-1 px-2 rounded-2xl border-2 border-indigo-200">
                <h3 className="text-xl font-bold text-indigo-800 mb-1">Editable Text</h3>
                <textarea
                  value={extractedText}
                  onChange={(e) => setExtractedText(e.target.value)}
                  rows={16}
                  className="w-full p-5 border-2 border-indigo-300 rounded-xl bg-white font-medium text-gray-800 resize-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 outline-none transition text-base leading-relaxed"
                  placeholder="Edit the extracted text here..."
                />
                <p className="text-sm text-indigo-600 mt-4 italic text-center">
                  You can freely edit and correct any OCR mistakes
                </p>
              </div>
            </div>
          )}

          {/* Bottom Buttons */}
          <div className="flex justify-end gap-5 mt-10">

            {/* Before extraction */}
            {!extractedText && (
              <>
                <button
                  onClick={resetAndClose}
                  className="px-10 py-4 bg-gray-600 hover:bg-gray-700 text-white text-lg font-bold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !image}
                  className="px-12 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 disabled:opacity-50 text-white text-lg font-bold rounded-xl transition flex items-center gap-3"
                >
                  {loading ? "Extracting..." : "Extract Text"}
                </button>
              </>
            )}

            {/* After extraction */}
            {extractedText && (
              <>
                <button
                  onClick={resetAndClose}
                  className="px-10 py-4 bg-gray-600 hover:bg-gray-700 text-white text-lg font-bold rounded-xl transition"
                >
                  Close
                </button>
                <button
                  onClick={handleUseText}
                  className="px-12 py-4 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white text-lg font-bold rounded-xl transition flex items-center gap-4"
                >
                  Submit
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;